import type { Job, Locale } from '@xphoto/shared';
import type { TFunction } from '../i18n';
import { interpolate } from '../gallery/format';
import { formatBytes } from '../viewer/format';

/**
 * Résumé PUR du RÉSULTAT d'un job terminé, sur une ligne, pour la cloche de
 * notifications. Plusieurs types portent un `result` structuré digne d'un résumé
 * lisible : `compress` (compressées + octets gagnés), `rotate` (pivotées), `move`
 * (déplacées), `trash_purge` (purgées), `rename_extensions` (renommées),
 * `empty_folders_cleanup` (dossiers supprimés). Tout autre type → `null` (aucune ligne
 * affichée) ; l'ensemble est EXTENSIBLE (ajouter un `case`).
 *
 * `job.result` est un jsonb LIBRE (`Record<string, unknown> | null | undefined`)
 * sérialisé au SEED (`GET /api/jobs`) et dans les événements SSE `finished`. On le
 * lit DÉFENSIVEMENT (via `numberField`) : un champ absent/non numérique/aberrant
 * (`NaN`, `Infinity`) est traité comme `0`, et la fonction ne LÈVE JAMAIS.
 *
 * L'INTERPOLATION des gabarits i18n reste ici (via `interpolate`) mais le TEXTE
 * vient des catalogues (`t`) → aucun libellé en dur. Les tailles passent par
 * `formatBytes(bytes, locale)` (base 1024, localisé).
 */
export function formatJobResult(job: Job, t: TFunction, locale: Locale): string | null {
  const result = job.result;
  if (result === null || result === undefined) return null;
  switch (job.type) {
    case 'compress':
      return formatCompress(result, job.dryRun, t, locale);
    case 'rotate':
      return formatRotate(result, t);
    case 'move':
      return formatMove(result, t);
    case 'trash_purge':
      return formatTrashPurge(result, t);
    case 'rename_extensions':
      return formatRenameExtensions(result, t);
    case 'empty_folders_cleanup':
      return formatEmptyFoldersCleanup(result, t);
    default:
      // Type sans résumé dédié : on n'affiche rien (comportement volontaire).
      return null;
  }
}

/** Champ numérique fini de `result` (jsonb libre) → nombre, ou `0` si absent/aberrant. */
function numberField(result: Record<string, unknown>, key: string): number {
  const value = result[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/**
 * Résumé d'une COMPRESSION. En dry-run : simulation du gain potentiel (au
 * conditionnel). Sinon : nombre de fichiers compressés + octets gagnés, ou un
 * libellé « aucun gain » si rien n'était plus petit. Ignorées/échecs annexés
 * seulement s'ils sont non nuls.
 */
function formatCompress(
  result: Record<string, unknown>,
  dryRun: boolean,
  t: TFunction,
  locale: Locale,
): string {
  const compressed = numberField(result, 'compressed');
  const bytesSaved = numberField(result, 'bytesSaved');
  const skipped = numberField(result, 'skipped');
  const failed = numberField(result, 'failed');
  const total = numberField(result, 'total');
  const size = formatBytes(bytesSaved, locale);

  let head: string;
  if (dryRun) {
    // Simulation : « gain potentiel X sur N » (aucun octet réellement gagné).
    head =
      compressed > 0
        ? interpolate(t('notifications.resultPotentialGain'), { bytes: size, total })
        : t('notifications.resultNoGain');
  } else if (compressed > 0) {
    head = `${interpolate(t('notifications.resultCompressed'), { count: compressed })} · ${interpolate(
      t('notifications.resultBytesSaved'),
      { bytes: size },
    )}`;
  } else {
    // Terminé mais rien n'était plus petit → aucun remplacement.
    head = t('notifications.resultNoGain');
  }

  const extras: string[] = [];
  if (skipped > 0) extras.push(interpolate(t('notifications.resultSkipped'), { count: skipped }));
  if (failed > 0) extras.push(interpolate(t('notifications.resultFailed'), { count: failed }));
  return extras.length > 0 ? `${head} · ${extras.join(' · ')}` : head;
}

/** Résumé d'une ROTATION : pivotées (+ ignorées / échecs si non nuls). */
function formatRotate(result: Record<string, unknown>, t: TFunction): string {
  const rotated = numberField(result, 'rotated');
  const skipped = numberField(result, 'skipped');
  const failed = numberField(result, 'failed');

  const parts = [interpolate(t('notifications.resultRotated'), { count: rotated })];
  if (skipped > 0) parts.push(interpolate(t('notifications.resultSkipped'), { count: skipped }));
  if (failed > 0) parts.push(interpolate(t('notifications.resultFailed'), { count: failed }));
  return parts.join(' · ');
}

/** Résumé d'un DÉPLACEMENT : déplacées (+ conflits / ignorées / échecs si non nuls). */
function formatMove(result: Record<string, unknown>, t: TFunction): string {
  const moved = numberField(result, 'moved');
  const conflicts = numberField(result, 'conflicts');
  const skipped = numberField(result, 'skipped');
  const failed = numberField(result, 'failed');

  const parts = [interpolate(t('notifications.resultMoved'), { count: moved })];
  if (conflicts > 0)
    parts.push(interpolate(t('notifications.resultConflicts'), { count: conflicts }));
  if (skipped > 0) parts.push(interpolate(t('notifications.resultSkipped'), { count: skipped }));
  if (failed > 0) parts.push(interpolate(t('notifications.resultFailed'), { count: failed }));
  return parts.join(' · ');
}

/** Résumé d'une CORRECTION D'EXTENSIONS : renommés (+ suffixes de collision / échecs). */
function formatRenameExtensions(result: Record<string, unknown>, t: TFunction): string {
  const renamed = numberField(result, 'renamed');
  const conflicts = numberField(result, 'conflicts');
  const failed = numberField(result, 'failed');

  const parts = [interpolate(t('notifications.resultRenamed'), { count: renamed })];
  if (conflicts > 0)
    parts.push(interpolate(t('notifications.resultConflicts'), { count: conflicts }));
  if (failed > 0) parts.push(interpolate(t('notifications.resultFailed'), { count: failed }));
  return parts.join(' · ');
}

/**
 * Résumé d'un NETTOYAGE DES DOSSIERS VIDES : dossiers supprimés (+ ignorés / échecs si
 * non nuls). « Ignoré » = redevenu non vide (ou déjà disparu) entre l'aperçu et le
 * `rmdir` — sans faute, mais on le dit plutôt que de laisser un écart inexpliqué.
 */
function formatEmptyFoldersCleanup(result: Record<string, unknown>, t: TFunction): string {
  const removed = numberField(result, 'removed');
  const skipped = numberField(result, 'skipped');
  const failed = numberField(result, 'failed');
  const filesRemoved = numberField(result, 'filesRemoved');

  const parts = [interpolate(t('notifications.resultFoldersRemoved'), { count: removed })];
  // Fichiers système effacés : ANNONCÉ dès qu'il y en a. C'est la seule suppression de
  // fichier du produit — elle ne doit pas se déduire du silence.
  if (filesRemoved > 0)
    parts.push(interpolate(t('notifications.resultSystemFilesRemoved'), { count: filesRemoved }));
  if (skipped > 0) parts.push(interpolate(t('notifications.resultSkipped'), { count: skipped }));
  if (failed > 0) parts.push(interpolate(t('notifications.resultFailed'), { count: failed }));
  return parts.join(' · ');
}

/** Résumé d'une PURGE DE CORBEILLE : purgées (+ manquants / échecs si non nuls). */
function formatTrashPurge(result: Record<string, unknown>, t: TFunction): string {
  const purged = numberField(result, 'purged');
  const missing = numberField(result, 'filesMissing');
  const failed = numberField(result, 'failed');

  const parts = [interpolate(t('notifications.resultPurged'), { count: purged })];
  if (missing > 0) parts.push(interpolate(t('notifications.resultMissing'), { count: missing }));
  if (failed > 0) parts.push(interpolate(t('notifications.resultFailed'), { count: failed }));
  return parts.join(' · ');
}
